import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';

const { FiArrowLeft, FiDollarSign, FiClock, FiTarget, FiTrendingUp, FiUsers, FiZap, FiCheck, FiStar, FiBarChart3 } = FiIcons;

const BenefitDetails = ({ benefitType, onBack }) => {
  const benefitData = {
    'Lower Startup Costs': {
      icon: FiDollarSign,
      title: 'Lower Startup Costs',
      subtitle: 'Reduce initial investment by 50-70% compared to traditional restaurants',
      heroStat: '$100K',
      heroLabel: 'Average startup cost vs $350K traditional',
      sections: [
        {
          title: 'Cost Breakdown Comparison',
          content: [
            { item: 'Kitchen Equipment', traditional: '$80,000', ghost: '$35,000', savings: '56%' },
            { item: 'Interior Design & Furniture', traditional: '$75,000', ghost: '$5,000', savings: '93%' },
            { item: 'Real Estate Deposit', traditional: '$50,000', ghost: '$15,000', savings: '70%' },
            { item: 'Permits & Licenses', traditional: '$15,000', ghost: '$8,000', savings: '47%' },
            { item: 'Initial Marketing', traditional: '$25,000', ghost: '$10,000', savings: '60%' },
            { item: 'Working Capital', traditional: '$105,000', ghost: '$27,000', savings: '74%' }
          ]
        },
        {
          title: 'Hidden Cost Advantages',
          content: [
            'No dining room construction or renovation costs',
            'Minimal interior design and furniture expenses',
            'Lower insurance premiums due to reduced liability',
            'No host/hostess or server hiring and training costs',
            'Reduced utility costs from smaller space requirements',
            'No expensive POS systems for table service'
          ]
        },
        {
          title: 'ROI Timeline',
          content: [
            { milestone: 'Month 1-3', description: 'Initial setup and equipment installation', cost: '$35,000' },
            { milestone: 'Month 4-6', description: 'Marketing launch and brand establishment', cost: '$15,000' },
            { milestone: 'Month 7-12', description: 'Break-even and initial profitability', revenue: '$180,000' },
            { milestone: 'Year 2', description: 'Full ROI achievement', revenue: '$450,000' }
          ]
        }
      ],
      testimonial: {
        quote: "We saved $200,000 in startup costs compared to opening a traditional restaurant. That money went straight into marketing and inventory, giving us a huge competitive advantage.",
        author: "Maria Santos",
        business: "Healthy Eats Express",
        result: "Break-even in 4 months"
      }
    },
    'Faster Time to Market': {
      icon: FiClock,
      title: 'Faster Time to Market',
      subtitle: 'Launch your food business in weeks, not months',
      heroStat: '30 Days',
      heroLabel: 'Average launch time vs 6+ months traditional',
      sections: [
        {
          title: 'Launch Timeline Comparison',
          content: [
            { phase: 'Permits & Approvals', traditional: '45-90 days', ghost: '10-15 days' },
            { phase: 'Space Preparation', traditional: '60-120 days', ghost: '7-14 days' },
            { phase: 'Equipment Installation', traditional: '30-45 days', ghost: '3-7 days' },
            { phase: 'Staff Hiring & Training', traditional: '30-60 days', ghost: '7-14 days' },
            { phase: 'Marketing Launch', traditional: '14-30 days', ghost: '3-7 days' },
            { phase: 'Soft Opening', traditional: '7-14 days', ghost: '1-3 days' }
          ]
        },
        {
          title: 'Speed Advantages',
          content: [
            'Pre-approved kitchen spaces eliminate lengthy permit processes',
            'Plug-and-play infrastructure reduces setup time',
            'Shared resources accelerate equipment installation',
            'Digital-first operations require minimal physical setup',
            'Delivery platform integration is immediate',
            'No dining room design or construction delays'
          ]
        },
        {
          title: 'First-Mover Benefits',
          content: [
            { benefit: 'Market Capture', description: 'Enter new markets before competitors establish presence' },
            { benefit: 'Brand Recognition', description: 'Build customer loyalty while competition is still setting up' },
            { benefit: 'Revenue Generation', description: 'Start earning revenue months ahead of traditional timeline' },
            { benefit: 'Data Collection', description: 'Begin gathering customer insights for optimization' }
          ]
        }
      ],
      testimonial: {
        quote: "From concept to first order in 28 days. Our traditional restaurant took 8 months to open. The speed advantage is incredible for cash flow and market positioning.",
        author: "David Kim",
        business: "Seoul Street Food",
        result: "First to market in 3 cities"
      }
    },
    'Multiple Brand Strategy': {
      icon: FiTarget,
      title: 'Multiple Brand Strategy',
      subtitle: 'Operate 3-5 different food brands from one kitchen',
      heroStat: '5 Brands',
      heroLabel: 'Average brands per successful ghost kitchen',
      sections: [
        {
          title: 'Multi-Brand Revenue Streams',
          content: [
            { brand: 'Premium Burgers', revenue: '$45,000/month', margin: '28%' },
            { brand: 'Healthy Bowls', revenue: '$38,000/month', margin: '32%' },
            { brand: 'Pizza & Wings', revenue: '$52,000/month', margin: '25%' },
            { brand: 'Asian Fusion', revenue: '$41,000/month', margin: '30%' },
            { brand: 'Desserts & Shakes', revenue: '$24,000/month', margin: '35%' }
          ]
        },
        {
          title: 'Strategic Advantages',
          content: [
            'Risk diversification across multiple cuisines and price points',
            'Capture different customer demographics and occasions',
            'Optimize kitchen utilization throughout different peak hours',
            'Test new concepts with minimal additional investment',
            'Cross-promote between brands for increased order value',
            'Seasonal flexibility to pivot focus between brands'
          ]
        },
        {
          title: 'Implementation Framework',
          content: [
            { step: 'Market Research', description: 'Identify underserved cuisines and price gaps in delivery market' },
            { step: 'Brand Development', description: 'Create distinct identities, menus, and positioning for each brand' },
            { step: 'Kitchen Optimization', description: 'Design workflow and equipment setup to handle multiple cuisines' },
            { step: 'Platform Management', description: 'Launch brands strategically across delivery platforms' },
            { step: 'Performance Tracking', description: 'Monitor metrics and optimize successful brands while pivoting others' }
          ]
        }
      ],
      testimonial: {
        quote: "Running 4 brands from one kitchen generates $180K monthly revenue. Each brand targets different customer segments, so we're never dependent on just one concept.",
        author: "Jennifer Lopez",
        business: "Multi-Concept Kitchen Co",
        result: "$2.1M annual revenue from single location"
      }
    },
    'Higher Profit Margins': {
      icon: FiTrendingUp,
      title: 'Higher Profit Margins',
      subtitle: 'Achieve 15-25% higher margins with reduced overhead',
      heroStat: '35%',
      heroLabel: 'Average profit margin vs 10-15% traditional',
      sections: [
        {
          title: 'Margin Comparison Analysis',
          content: [
            { category: 'Food Costs', traditional: '28-32%', ghost: '25-28%', improvement: '3-4%' },
            { category: 'Labor Costs', traditional: '30-35%', ghost: '18-22%', improvement: '12-13%' },
            { category: 'Rent & Utilities', traditional: '10-15%', ghost: '6-8%', improvement: '4-7%' },
            { category: 'Marketing', traditional: '3-5%', ghost: '8-12%', improvement: 'Strategic investment' },
            { category: 'Net Profit', traditional: '3-8%', ghost: '15-25%', improvement: '12-17%' }
          ]
        },
        {
          title: 'Cost Optimization Strategies',
          content: [
            'Eliminate front-of-house labor costs (servers, hosts, bussers)',
            'Reduce food waste through data-driven demand forecasting',
            'Negotiate better ingredient costs through shared purchasing power',
            'Optimize delivery radius to minimize logistics costs',
            'Implement dynamic pricing based on demand patterns',
            'Reduce overhead through shared kitchen infrastructure'
          ]
        },
        {
          title: 'Revenue Maximization',
          content: [
            { strategy: 'Peak Hour Optimization', impact: 'Increase revenue per hour by 40-60%' },
            { strategy: 'Upselling Algorithms', impact: 'Boost average order value by 15-25%' },
            { strategy: 'Customer Retention', impact: 'Increase lifetime value by 200-300%' },
            { strategy: 'Dynamic Menu Pricing', impact: 'Optimize margins based on demand and costs' }
          ]
        }
      ],
      testimonial: {
        quote: "Our ghost kitchen operates at 32% profit margins compared to 8% at our traditional restaurant. The elimination of front-of-house costs was a game-changer.",
        author: "Roberto Martinez",
        business: "Authentic Tacos Co",
        result: "4x higher profit margins"
      }
    },
    'Data-Driven Growth': {
      icon: FiUsers,
      title: 'Data-Driven Growth',
      subtitle: 'Access rich customer data to optimize operations',
      heroStat: '10x',
      heroLabel: 'More customer data than traditional restaurants',
      sections: [
        {
          title: 'Available Data Metrics',
          content: [
            { metric: 'Order Patterns', insight: 'Peak hours, seasonal trends, weather impact' },
            { metric: 'Customer Preferences', insight: 'Popular items, dietary restrictions, price sensitivity' },
            { metric: 'Geographic Analysis', insight: 'Delivery zones, neighborhood preferences, competition' },
            { metric: 'Operational Efficiency', insight: 'Prep times, delivery speed, kitchen utilization' },
            { metric: 'Marketing Performance', insight: 'Campaign ROI, customer acquisition cost, retention rates' }
          ]
        },
        {
          title: 'Optimization Applications',
          content: [
            'Menu engineering based on profitability and popularity data',
            'Inventory management to reduce waste and stockouts',
            'Staff scheduling aligned with predicted demand patterns',
            'Dynamic pricing to maximize revenue during peak periods',
            'Targeted marketing campaigns for customer segments',
            'New location selection based on demographic analysis'
          ]
        },
        {
          title: 'Competitive Intelligence',
          content: [
            { advantage: 'Market Gap Analysis', description: 'Identify underserved cuisines and price points in real-time' },
            { advantage: 'Customer Behavior Insights', description: 'Understand ordering patterns and preferences better than competitors' },
            { advantage: 'Performance Benchmarking', description: 'Compare metrics against industry standards and top performers' },
            { advantage: 'Predictive Analytics', description: 'Forecast demand and optimize operations proactively' }
          ]
        }
      ],
      testimonial: {
        quote: "The data insights from our delivery platforms increased our revenue by 40% in 6 months. We know exactly what customers want, when they want it, and how much they'll pay.",
        author: "Lisa Chen",
        business: "Data Kitchen Analytics",
        result: "40% revenue increase through optimization"
      }
    },
    'Rapid Scalability': {
      icon: FiZap,
      title: 'Rapid Scalability',
      subtitle: 'Scale to new locations quickly with proven models',
      heroStat: '3x',
      heroLabel: 'Faster expansion than traditional restaurants',
      sections: [
        {
          title: 'Scaling Timeline Comparison',
          content: [
            { milestone: 'Location 2', traditional: '12-18 months', ghost: '2-4 months' },
            { milestone: 'Location 5', traditional: '3-5 years', ghost: '8-12 months' },
            { milestone: 'Location 10', traditional: '5-8 years', ghost: '18-24 months' },
            { milestone: 'Multi-Market', traditional: '8-12 years', ghost: '2-3 years' }
          ]
        },
        {
          title: 'Scaling Advantages',
          content: [
            'Standardized kitchen operations reduce setup complexity',
            'Digital systems easily replicate across locations',
            'Lower capital requirements enable faster expansion',
            'Proven concepts reduce new location risk',
            'Shared resources and economies of scale',
            'Remote management capabilities for multi-location oversight'
          ]
        },
        {
          title: 'Expansion Strategies',
          content: [
            { strategy: 'Hub and Spoke Model', description: 'Central commissary kitchen feeding multiple micro-locations' },
            { strategy: 'Franchise Partnership', description: 'License proven concepts to local operators' },
            { strategy: 'Market Penetration', description: 'Multiple brands in same market to capture larger share' },
            { strategy: 'Geographic Expansion', description: 'Replicate successful models in similar demographics' }
          ]
        }
      ],
      testimonial: {
        quote: "We scaled from 1 to 15 locations in 2 years. With traditional restaurants, that would have taken 8+ years and millions more in capital investment.",
        author: "Michael Johnson",
        business: "Rapid Growth Kitchens",
        result: "15 locations in 24 months"
      }
    }
  };

  const benefit = benefitData[benefitType];

  if (!benefit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Benefit not found</h1>
          <button 
            onClick={onBack}
            className="bg-purple-600 hover:bg-purple-700 px-6 py-2 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo size="md" />
            <span className="text-white font-bold text-xl">Ghost Kitchen Food</span>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-white hover:text-purple-300 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Overview</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <SafeIcon icon={benefit.icon} className="text-white text-3xl" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {benefit.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {benefit.subtitle}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto border border-white/20">
              <div className="text-4xl font-bold text-purple-400 mb-2">
                {benefit.heroStat}
              </div>
              <div className="text-gray-300">
                {benefit.heroLabel}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {benefit.sections.map((section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: sectionIndex * 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                {section.title}
              </h2>

              {/* Render different content types */}
              {Array.isArray(section.content) && typeof section.content[0] === 'string' ? (
                // Simple list
                <div className="grid md:grid-cols-2 gap-4">
                  {section.content.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              ) : section.content[0].item ? (
                // Cost breakdown table
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left text-white font-semibold py-3">Cost Category</th>
                        <th className="text-right text-white font-semibold py-3">Traditional</th>
                        <th className="text-right text-white font-semibold py-3">Ghost Kitchen</th>
                        <th className="text-right text-white font-semibold py-3">Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.content.map((row, index) => (
                        <tr key={index} className="border-b border-white/10">
                          <td className="text-gray-300 py-3">{row.item}</td>
                          <td className="text-right text-red-300 py-3">{row.traditional}</td>
                          <td className="text-right text-green-300 py-3">{row.ghost}</td>
                          <td className="text-right text-purple-300 font-semibold py-3">{row.savings}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : section.content[0].phase ? (
                // Timeline comparison
                <div className="space-y-4">
                  {section.content.map((item, index) => (
                    <div key={index} className="grid md:grid-cols-3 gap-4 bg-white/5 rounded-lg p-4">
                      <div className="text-white font-semibold">{item.phase}</div>
                      <div className="text-red-300">Traditional: {item.traditional}</div>
                      <div className="text-green-300">Ghost Kitchen: {item.ghost}</div>
                    </div>
                  ))}
                </div>
              ) : (
                // Other content types (objects with various structures)
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.content.map((item, index) => (
                    <div key={index} className="bg-white/5 rounded-lg p-6 border border-white/10">
                      {item.brand && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.brand}</h3>
                          <div className="text-green-400 font-bold mb-1">{item.revenue}</div>
                          <div className="text-purple-300 text-sm">{item.margin} margin</div>
                        </>
                      )}
                      {item.step && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.step}</h3>
                          <p className="text-gray-300 text-sm">{item.description}</p>
                        </>
                      )}
                      {item.milestone && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.milestone}</h3>
                          <p className="text-gray-300 text-sm mb-2">{item.description}</p>
                          {item.cost && <div className="text-red-300 text-sm">Investment: {item.cost}</div>}
                          {item.revenue && <div className="text-green-400 text-sm">Revenue: {item.revenue}</div>}
                        </>
                      )}
                      {item.category && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.category}</h3>
                          <div className="text-red-300 text-sm mb-1">Traditional: {item.traditional}</div>
                          <div className="text-green-300 text-sm mb-1">Ghost Kitchen: {item.ghost}</div>
                          <div className="text-purple-300 text-sm font-semibold">Improvement: {item.improvement}</div>
                        </>
                      )}
                      {item.metric && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.metric}</h3>
                          <p className="text-gray-300 text-sm">{item.insight}</p>
                        </>
                      )}
                      {item.benefit && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.benefit}</h3>
                          <p className="text-gray-300 text-sm">{item.description}</p>
                        </>
                      )}
                      {item.strategy && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.strategy}</h3>
                          <p className="text-gray-300 text-sm">{item.description || item.impact}</p>
                        </>
                      )}
                      {item.advantage && (
                        <>
                          <h3 className="text-white font-semibold mb-2">{item.advantage}</h3>
                          <p className="text-gray-300 text-sm">{item.description}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}

          {/* Testimonial Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-8 border border-purple-400/30 text-center"
          >
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-xl" />
              ))}
            </div>
            <blockquote className="text-xl text-gray-200 italic mb-6 max-w-4xl mx-auto">
              "{benefit.testimonial.quote}"
            </blockquote>
            <div className="text-white font-semibold mb-1">
              {benefit.testimonial.author}
            </div>
            <div className="text-purple-300 text-sm mb-2">
              {benefit.testimonial.business}
            </div>
            <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full inline-block text-sm font-semibold">
              {benefit.testimonial.result}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Experience These Benefits?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of successful ghost kitchen operators who chose the smart path to food business success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onBack}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
              >
                Explore All Benefits
              </button>
              <button className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300">
                Get Started Today
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BenefitDetails;